import React, { useState } from 'react'
import { Row, Col } from 'react-flexbox-grid'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

import { downloadFromUrlPromise } from 'helpers/downloadFromUrl'
import ContentBlock from 'components/ContentBlock'
import { Form, Select, DateTimePicker, Text } from 'components/forms'
import Button from 'components/Button'
import Divider from 'components/Divider'
import { WarningModal, ServiceModal } from 'components/Modal'
import { useLazyQuery, useQuery } from 'components/graphql'
import { listShops } from 'gql/query'

import exportAbacusQuery from '../../gql/exportAbacus'
import downloadAbacusQuery from '../../gql/downloadAbacus'
import getAppConfig from '../../gql/getAppConfig'
import { resolveMultiShopIds } from './utils'

const exportOptions = [
	{ label: 'Compressed', value: '0' },
	{ label: 'Extended', value: '1' }
]

const Abacus = () => {
	const [t] = useTranslation()
	const [selectedExportOption, setSelectedExportOption] = useState('0')

	const [formData, setFormData] = useState({
		abacusShops: null,
		datePeriod: {
			from: moment().subtract(1, 'months').utc().startOf('month').toISOString(),
			to: moment().subtract(1, 'months').utc().endOf('month').toISOString()
		}
	})

	const [hasError, setHasError] = useState(false)
	const [warningMessages, setWarningMessages] = useState([])
	const [exportErrors, setExportErrors] = useState(null)

	const { data: shopsData } = useQuery(listShops)
	const { data: appConfigData } = useQuery(getAppConfig, { fetchPolicy: 'cache-first' })

	const shopItems = shopsData?.listShops?.items || []
	const allShopsOption = shopItems.length > 0
		? { id: JSON.stringify(shopItems.map(o => o.id)), name: t('statistic_section.strings.all_shops') }
		: null
	const shopOptions = allShopsOption ? [allShopsOption, ...shopItems] : []

	const savedEmail = appConfigData?.getAppConfig?.cashRegisterGlobal?.savedEmail
	const effectiveEmail = formData.email ?? savedEmail ?? ''
	const effectiveShops = formData.abacusShops ?? (allShopsOption ? [allShopsOption] : [])

	const [sendAbacus, { loading: isSendLoading }] = useLazyQuery(exportAbacusQuery)
	const [fetchDownload, { loading: isDownloadLoading }] = useLazyQuery(downloadAbacusQuery)

	const handleShopsChange = selectedOptions => {
		if (!selectedOptions || selectedOptions.length === 0) {
			setFormData(prev => ({ ...prev, abacusShops: allShopsOption ? [allShopsOption] : [] }))
			return
		}
		const lastSelected = selectedOptions[selectedOptions.length - 1]
		if (lastSelected.id === allShopsOption?.id) {
			setFormData(prev => ({ ...prev, abacusShops: [allShopsOption] }))
		} else {
			setFormData(prev => ({ ...prev, abacusShops: selectedOptions.filter(o => o.id !== allShopsOption?.id) }))
		}
	}

	const handleSend = async () => {
		const result = await sendAbacus({
			variables: {
				shopId: resolveMultiShopIds(effectiveShops),
				email: effectiveEmail,
				datePeriod: formData.datePeriod,
				detailedExport: Boolean(Number(selectedExportOption))
			}
		})
		if (!result?.data) {
			return
		}
		const { success, messages, error } = result.data.cashAssist.sendAbacus
		if (error) {
			setHasError(true)
		}
		if (!success && messages?.length) {
			setWarningMessages(messages)
		}
	}

	const handleDownload = async () => {
		const result = await fetchDownload({
			variables: {
				shopId: resolveMultiShopIds(effectiveShops),
				datePeriod: formData.datePeriod,
				detailedExport: Boolean(Number(selectedExportOption))
			}
		})
		if (!result?.data) {
			return
		}
		const { link: linkMain, linkCreditor, messages } = result.data.cashAssist.downloadAbacus
		if (linkMain) {
			await downloadFromUrlPromise(linkMain, 'abacusExport.xml')
		}
		if (linkCreditor) {
			await downloadFromUrlPromise(linkCreditor, 'abacusExport2nd.xml')
		}
		if (!linkMain || !linkCreditor) {
			setExportErrors(messages || null)
		}
	}

	return (
		<ContentBlock label='Abacus'>
			{
				!!hasError && <WarningModal
					onClose={() => setHasError(false)}
					label={t('options_section.export.header')}
					text={`${t('common.strings.no_closings')}`}
				/>
			}
			{
				!!warningMessages?.length && <WarningModal
					onClose={() => setWarningMessages([])}
					label={t('options_section.export.header')}
					text={`${t('options_section.export.text', { items: warningMessages.join(', ') })}`}
				/>
			}
			<Form>
				<Row>
					<Col xs>
						<Select
							noForm
							value={effectiveShops}
							simpleValue={false}
							isMulti
							closeMenuOnSelect={false}
							label={t('common.strings.branch')}
							onChange={handleShopsChange}
							options={shopOptions}
							valueField='id'
							labelField='name'
							isSearchable
							validate={val => (Array.isArray(val) && val.length > 0) ? null : t('validators.required')}
							validateOnChange
						/>
						<Text
							noForm
							value={effectiveEmail}
							onChange={({ target }) => setFormData(prev => ({ ...prev, email: target.value }))}
							label={t('common.fields.email')}
							validate={val => val ? null : t('validators.required')}
							validateOnChange
						/>
					</Col>
					<Col xs>
						<DateTimePicker
							noForm
							value={formData.datePeriod}
							onChange={datePeriod => setFormData(prev => ({ ...prev, datePeriod }))}
							label={t('common.fields.date')}
							validate={val => val ? null : t('validators.required')}
							validateOnChange
							interval
						/>
					</Col>
					<Col xs>
						<Select
							noForm
							value={selectedExportOption}
							label='Extend export by taxes'
							onChange={newValue => setSelectedExportOption(newValue.value)}
							options={exportOptions}
							valueField='value'
							labelField='label'
						/>
					</Col>
				</Row>
				<Divider />
				<Button
					status={effectiveShops.length > 0 && effectiveEmail ? 'primary' : 'inactive'}
					text={t('message_section.strings.send')}
					loading={isSendLoading}
					onClick={handleSend}
				/>
				<Button
					status={effectiveShops.length > 0 ? 'primary' : 'inactive'}
					text={t('common.buttons.download')}
					loading={isDownloadLoading}
					onClick={handleDownload}
				/>
			</Form>
			{exportErrors?.length &&
				<ServiceModal
					type='confirm'
					onOk={() => {
						setExportErrors(null)
					}}
					onClose={() => {
						setExportErrors(null)
					}}
					text={exportErrors?.join('. ')}
					btnText={t('common.buttons.confirm')}
					btnCancelText={t('common.buttons.cancel')}
				/>
			}
		</ContentBlock>
	)
}

export default Abacus
